import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_Equipment_FindAllForCompanyList_VM } from 'ab-api/web/equipment/findAllForCompanyList';
import type { W_ReportType_Edit_RM, W_ReportType_Edit_RM_ReportBlockField, W_ReportType_Edit_RM_ReportTypeBlock } from 'ab-api/web/reportType/edit';
import type { W_ReportType_Find_VM } from 'ab-api/web/reportType/find';

import type ReportBlockMapVM from 'ab-viewModels/reportBlock/reportBlockMap.viewModel';

import { flattenElements } from '@acceligentllc/shared/utils/array';

import type { RootState } from 'af-reducers';

import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';

import * as EquipmentActions from 'af-actions/equipment';
import * as ReportBlockActions from 'af-actions/reportBlock';
import * as ReportTypeActions from 'af-actions/reportType';

import { ReportBlockFieldFormModel } from '../../Shared/formModel';
import { ReportTypePreviewHighlightContext, useReportTypePreviewHighlightContextState } from '../Context/ReportTypePreviewHighlight';
import type { RequestModelMapperContext } from '../CustomTypeForm';
import ReportTypeForm from '../CustomTypeForm';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const getFieldFormModelToRequestModelMapper = (reportFieldsByIdMap: { [id: string]: ReportBlockFieldFormModel; }) => {
	return (id: string, index: number): W_ReportType_Edit_RM_ReportBlockField => {
		const field = reportFieldsByIdMap[id];
		return ReportBlockFieldFormModel.toEditReportTypeReportBlockFieldRM(field, index);
	};
};

/** Maps report type block Form Models to RMs and adds calculation field options */
const getReportTypeBlockFormModelToRequestModelMapper = (
	updateIndex: boolean,
	reportTypeBlocksByIdMap: RequestModelMapperContext['reportTypeBlocksByIdMap'],
	reportBlocksByIdMap: RequestModelMapperContext['reportBlocksByIdMap'],
	reportFieldsByIdMap: RequestModelMapperContext['reportFieldsByIdMap']
) => {
	return (id: string, index: Nullable<number>): W_ReportType_Edit_RM_ReportTypeBlock => {
		const fieldMapper = getFieldFormModelToRequestModelMapper(reportFieldsByIdMap);
		const typeBlock = reportTypeBlocksByIdMap[id];
		const reportBlock = reportBlocksByIdMap[typeBlock.reportBlockVirtualId];

		return {
			...typeBlock,
			reportTypeId: typeBlock.reportTypeId ?? undefined,
			reportBlockId: typeBlock.reportBlockId,
			index: updateIndex ? index : null,
			reportBlock: {
				...reportBlocksByIdMap[typeBlock.reportBlockVirtualId],
				reportBlockFields: (reportBlock.reportBlockFieldIds ?? []).map(fieldMapper),
			},
		};
	};
};

const toRequestModel = (context: RequestModelMapperContext) => {
	const {
		billableWorks,
		primaryTypeBlockIds,
		secondaryTypeBlockIds,
		primaryTypeMainBlockId,
		secondaryTypeMainBlockId,
		upperTotalBlockId,
		lowerTotalBlockId,
		reportTypeName,
		reportTypeDescription,
		reportTypeBlocksByIdMap,
		reportBlocksByIdMap,
		reportFieldsByIdMap,
	} = context;

	const reportTypeBlocks: W_ReportType_Edit_RM_ReportTypeBlock[] = [];

	// Creates Report Type Block VM to RM mappers
	const reportTypeBlocksRMMapper = getReportTypeBlockFormModelToRequestModelMapper(
		true, reportTypeBlocksByIdMap, reportBlocksByIdMap, reportFieldsByIdMap
	);
	const mainReportTypeBlocksRMMapper = getReportTypeBlockFormModelToRequestModelMapper(
		false, reportTypeBlocksByIdMap, reportBlocksByIdMap, reportFieldsByIdMap
	);

	const primaryIds = flattenElements(primaryTypeMainBlockId, primaryTypeBlockIds);
	const secondaryIds = flattenElements(secondaryTypeMainBlockId, secondaryTypeBlockIds);

	reportTypeBlocks.push(...primaryIds.map(reportTypeBlocksRMMapper));
	reportTypeBlocks.push(...secondaryIds.map(reportTypeBlocksRMMapper));

	if (upperTotalBlockId) {
		reportTypeBlocks.push(mainReportTypeBlocksRMMapper(upperTotalBlockId, null));
	}
	if (lowerTotalBlockId) {
		reportTypeBlocks.push(mainReportTypeBlocksRMMapper(lowerTotalBlockId, null));
	}

	return {
		name: reportTypeName,
		reportTypeBlocks,
		description: reportTypeDescription,
		billableWorks,
	};
};

const EditReportType: React.FC<Props> = (props: Props) => {
	const {
		location: { state: { orgAlias } },
		companyName,
		fetchEquipmentList,
		fetchReportBlocks,
		fetchReportType,
		match: { params: { id } },
		edit,
		history,
	} = props;

	const reportTypePreviewHighlight = useReportTypePreviewHighlightContextState();

	const [blocks, setBlocks] = React.useState<ReportBlockMapVM>({ blockMap: {}, fieldMap: {} });
	const [reportType, setReportType] = React.useState<Nullable<W_ReportType_Find_VM>>(null);
	const [listOfEquipment, setListOfEquipment] = React.useState<Nullable<W_Equipment_FindAllForCompanyList_VM>>(null);
	const [fetchingBlocks, setFetchingBlocks] = React.useState(true);

	const isSubmittingRef = React.useRef(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				setFetchingBlocks(true);
				const _reportType = await fetchReportType(+id);
				const _blocks = await fetchReportBlocks();
				setBlocks(_blocks);
				setReportType(_reportType);
			} finally {
				setFetchingBlocks(false);
			}
		};
		fetchData();
	}, [fetchReportType, fetchReportBlocks, id]);

	const fetchListOfEquipment = React.useCallback(async () => {
		const equipment = await fetchEquipmentList('');
		setListOfEquipment(equipment);
	}, [fetchEquipmentList]);

	const onEdit = React.useCallback(async (form: W_ReportType_Edit_RM) => {
		if (isSubmittingRef.current) {
			return;
		}

		isSubmittingRef.current = true;

		try {
			await edit(+id, form);
			history.push(CLIENT.COMPANY.SETTINGS.REPORT.TABLE(orgAlias, companyName));
		} finally {
			isSubmittingRef.current = false;
		}
	}, [edit, id, history, orgAlias, companyName]);

	const breadcrumbs = React.useMemo(() => [
		{ label: 'Report Type Elements', url: CLIENT.COMPANY.SETTINGS.REPORT.TABLE(orgAlias, companyName) },
		{ label: 'Edit Report Type' },
	], [companyName, orgAlias]);

	return (
		<ReportTypePreviewHighlightContext.Provider value={reportTypePreviewHighlight}>
			<div className="form-segment">
				<Breadcrumbs items={breadcrumbs} />
				<ReportTypeForm<W_ReportType_Edit_RM>
					fetchListOfEquipment={fetchListOfEquipment}
					hasBillableWork
					listOfEquipment={listOfEquipment}
					loading={fetchingBlocks || !reportType}
					onSubmit={onEdit}
					reportBlockFieldsMap={blocks.fieldMap}
					reportBlocksMap={blocks.blockMap}
					reportType={reportType}
					requestModelMapper={toRequestModel}
				/>
			</div>
		</ReportTypePreviewHighlightContext.Provider>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		edit: ReportTypeActions.editCustom,
		fetchReportBlocks: ReportBlockActions.findList,
		fetchReportType: ReportTypeActions.findCustomById,
		fetchEquipmentList: EquipmentActions.findAllForCompanyList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector
);

export default enhance(EditReportType);
